import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  ArrowLeft, MapPin, Calendar, FileText, Download, Bell, BarChart3, Ruler,
  AlertCircle, Loader2, RefreshCcw,
} from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api';
import {
  useInspection, useInspectionJob, useReprocessInspection,
} from '../hooks/api/inspections';
import { usePermission } from '../hooks/useMe';

// Demo image set for the carousel when an inspection has no image files yet.
const PLACEHOLDER_IMAGES = [
  { url: 'https://images.unsplash.com/photo-1531431057391-da7a1aabd412?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    title: 'Site Overview',  description: 'Aerial view of the site' },
  { url: 'https://images.unsplash.com/photo-1568671566370-49b36c5c7805?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    title: 'Structural Detail', description: 'Detailed structural analysis' },
  { url: 'https://images.unsplash.com/photo-1707405644816-2d48b6a3b8d0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    title: 'Surface Analysis',  description: 'Surface condition assessment' },
  { url: 'https://images.unsplash.com/photo-1671959541798-878565a4c103?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    title: 'Equipment Status',  description: 'Drone & sensor deployment' },
];

function shortDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
}

export function ProjectDetail({ inspectionId, onBack }) {
  const { data: insp, isLoading, isError, error } = useInspection(inspectionId);
  const { data: job } = useInspectionJob(inspectionId);
  const reprocess = useReprocessInspection();
  const canReprocess = usePermission('inspection:reprocess');

  const filesQuery = useQuery({
    queryKey: ['inspection-files', inspectionId],
    queryFn: () => apiGet(`/uploads/inspection/${inspectionId}/files`),
    enabled: Boolean(inspectionId),
  });

  // Build the carousel from real image files if available, else placeholders.
  const carousel = useMemo(() => {
    const imgs = (filesQuery.data || []).filter((f) =>
      (f.mime_type || '').startsWith('image/') || f.file_type === 'image',
    );
    if (imgs.length > 0) {
      return imgs.map((f) => ({
        url: f.download_url,
        title: f.original_filename || 'Inspection image',
        description: f.file_type || 'image',
      }));
    }
    return PLACEHOLDER_IMAGES;
  }, [filesQuery.data]);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const currentImage = carousel[currentImageIndex] || carousel[0];

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 justify-center text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading site…
      </div>
    );
  }
  if (isError || !insp) {
    return (
      <div className="space-y-3">
        <Button variant="outline" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
        <div className="py-6 text-sm text-destructive flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error?.message || 'Inspection not found'}
        </div>
      </div>
    );
  }

  const progress = job?.progress ?? insp.progress ?? 0;
  const state = job?.state || insp.status;
  const meta = insp.metadata || {};
  const location = meta.location || '—';
  const distance = meta.distance || meta.area || '—';
  const defects = meta.defects_count != null ? `${meta.defects_count} defects` : '—';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Sites
        </Button>
        {canReprocess && state !== 'processing' && (
          <Button
            variant="outline"
            size="sm"
            disabled={reprocess.isPending}
            onClick={() => reprocess.mutate(insp.id)}
          >
            <RefreshCcw className="w-4 h-4 mr-2" /> Reprocess
          </Button>
        )}
      </div>

      {/* Full Screen Image */}
      <Card className="border-0 bg-card overflow-hidden">
        <div className="relative w-full h-[70vh] bg-black">
          {currentImage && (
            <ImageWithFallback
              src={currentImage.url}
              alt={currentImage.title}
              className="w-full h-full object-contain"
            />
          )}

          {/* Image Navigation Thumbnails */}
          {carousel.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center space-x-2 bg-black/60 backdrop-blur-sm rounded-lg p-2 max-w-[90%] overflow-x-auto">
              {carousel.map((image, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-16 h-16 rounded overflow-hidden border-2 transition-all shrink-0 ${
                    index === currentImageIndex
                      ? 'border-blue-500 scale-110'
                      : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img src={image.url} alt={image.title} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Image Info Overlay */}
          <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg p-3 max-w-[60%]">
            <h3 className="text-white font-medium truncate">{insp.name}</h3>
            <p className="text-white/80 text-sm truncate">
              {currentImage?.description || 'Inspection details'}
            </p>
          </div>
        </div>

        {/* Site Details */}
        <CardContent className="border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 py-4">
            <div className="flex items-start space-x-3">
              <MapPin className="w-5 h-5 text-blue-500 mt-1" />
              <div>
                <div className="text-sm text-muted-foreground">Location</div>
                <div className="font-medium">{location}</div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Calendar className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <div className="text-sm text-muted-foreground">Inspected Date</div>
                <div className="font-medium">{shortDate(insp.completed_at || insp.started_at || insp.created_at)}</div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Ruler className="w-5 h-5 text-purple-500 mt-1" />
              <div>
                <div className="text-sm text-muted-foreground">Total Distance</div>
                <div className="font-medium">{distance}</div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-orange-500 mt-1" />
              <div>
                <div className="text-sm text-muted-foreground">Defects Identified</div>
                <div className="font-medium">{defects}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Card className="border-0 bg-card">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2">
              <FileText className="w-6 h-6 text-blue-500" />
              <span>Reports</span>
            </Button>
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2">
              <Bell className="w-6 h-6 text-orange-500" />
              <span>Alerts</span>
            </Button>
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2">
              <BarChart3 className="w-6 h-6 text-green-500" />
              <span>Analytics</span>
            </Button>
            <Button className="h-20 flex flex-col items-center justify-center space-y-2 bg-primary hover:bg-primary/90">
              <Download className="w-6 h-6" />
              <span>Download Report</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Project Metrics + Recent Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-0 bg-card">
          <CardHeader>
            <CardTitle>Inspection Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">Status</span>
                <span className="font-semibold capitalize">{state}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">Progress</span>
                <span className="font-semibold text-blue-500">{progress}%</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">Files uploaded</span>
                <span className="font-semibold">{filesQuery.data?.length ?? 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">Created</span>
                <span className="font-semibold">{shortDate(insp.created_at)}</span>
              </div>
              {job?.error && (
                <div className="flex items-center justify-between p-3 bg-destructive/10 rounded-lg text-destructive text-sm">
                  <span>Last error</span>
                  <span className="font-medium truncate ml-2">{job.error}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-card">
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {state === 'processing' && (
                <ActivityRow color="bg-blue-500"
                  title={`Pipeline running — ${job?.step || 'in flight'}`}
                  meta={`Progress ${progress}% • polling every 1.5s`} />
              )}
              {state === 'done' && (
                <ActivityRow color="bg-green-500"
                  title="Inspection completed"
                  meta={`Finished ${shortDate(insp.completed_at)}`} />
              )}
              {state === 'failed' && (
                <ActivityRow color="bg-red-500"
                  title="Pipeline failed"
                  meta={job?.error || 'Check the inspection logs'} />
              )}
              {filesQuery.data?.length > 0 && (
                <ActivityRow color="bg-yellow-500"
                  title={`${filesQuery.data.length} file${filesQuery.data.length === 1 ? '' : 's'} uploaded`}
                  meta="Use the carousel above to browse" />
              )}
              {!state && filesQuery.isLoading && (
                <div className="text-sm text-muted-foreground">Loading activity…</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ActivityRow({ color, title, meta }) {
  return (
    <div className="flex items-start space-x-3 p-3 rounded-lg bg-muted/50">
      <div className={`w-2 h-2 ${color} rounded-full mt-2`}></div>
      <div className="flex-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{meta}</p>
      </div>
    </div>
  );
}
